import { useState, useEffect } from 'react';
import { Save, Plus, CheckCircle, AlertTriangle, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import '../../tailwindcss/Tailwind.css';
import yaml from 'js-yaml';

import NewConfigModal from './components/NewConfigModal.js';
import InstructionsTab from './components/InstructionsTab.js';
import PromptTemplatesTab from './components/PromptTemplatesTab.js';
import GuardrailsService from './api.js';

const GuardrailsManager = () => {
  const [configurations, setConfigurations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewConfigModal, setShowNewConfigModal] = useState(false);
  const [newConfigName, setNewConfigName] = useState('');

  // Inline editor state
  const [expandedConfig, setExpandedConfig] = useState(null);

  // Editor state
  const [activeTab, setActiveTab] = useState('instructions');
  const [saveStatus, setSaveStatus] = useState(null);
  const [isEditorLoading, setIsEditorLoading] = useState(false);
  const [configData, setConfigData] = useState({
    models: [],
    instructions: [],
    rails: {
      input: { flows: [] },
      output: { flows: [], streaming: { enabled: true, chunk_size: 200, context_size: 50 } }
    },
    streaming: true,
    streaming_usage: true
  });
  const [promptTemplates, setPromptTemplates] = useState({
    self_check_input: '',
    self_check_output: '',
    self_check_hallucination: ''
  });

  // Test state
  const [testInput, setTestInput] = useState('');
  const [testType, setTestType] = useState('input');
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);

  useEffect(() => {
    fetchConfigurations();
  }, []);

  const fetchConfigurations = async () => {
    try {
      setIsLoading(true);
      const data = await GuardrailsService.getAllConfigurations();
      setConfigurations(data);
    } catch (error) {
      console.error('Error fetching configurations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchConfiguration = async (configName) => {
    try {
      setIsEditorLoading(true);
      const data = await GuardrailsService.getConfiguration(configName);
      setConfigData(data);
      await fetchPromptTemplates(configName);
    } catch (error) {
      console.error('Error fetching configuration:', error);
    } finally {
      setIsEditorLoading(false);
    }
  };

  const fetchPromptTemplates = async (configName) => {
    try {
      const templates = {};
      const types = ['self_check_input', 'self_check_output', 'self_check_hallucination'];
      for (const type of types) {
        try {
          const data = await GuardrailsService.getPromptTemplate(configName, type);
          const content = data.content;
          let parsed;
          try {
            if (typeof content === 'string' && content.trim().startsWith('{')) {
              parsed = JSON.parse(content);
            } else {
              parsed = content;
            }
            templates[type] = yaml.dump(parsed);
          } catch (err) {
            templates[type] = content || '';
          }
        } catch (err) {
          templates[type] = '';
        }
      }
      setPromptTemplates(templates);
    } catch (error) {
      console.error('Error fetching prompt templates:', error);
    }
  };

  const handleEditConfig = async (configName) => {
    if (expandedConfig === configName) {
      setExpandedConfig(null);
      return;
    }
    setExpandedConfig(configName);
    setActiveTab('instructions');
    setSaveStatus(null);
    setTestResult(null);
    await fetchConfiguration(configName);
  };

  const handleDeleteConfig = async (configName) => {
    if (!window.confirm(`Are you sure you want to delete the "${configName}" configuration?`)) return;
    try {
      setIsLoading(true);
      await GuardrailsService.deleteConfiguration(configName);
      if (expandedConfig === configName) setExpandedConfig(null);
      await fetchConfigurations();
    } catch (error) {
      console.error('Error deleting configuration:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateConfig = async () => {
    if (!newConfigName.trim()) return;
    try {
      setIsLoading(true);
      await GuardrailsService.createConfiguration(newConfigName);
      setShowNewConfigModal(false);
      setNewConfigName('');
      await fetchConfigurations();
    } catch (error) {
      console.error('Error creating configuration:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setIsEditorLoading(true);
      setSaveStatus(null);
      await GuardrailsService.updateConfiguration(expandedConfig, configData);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error saving configuration:', error);
      setSaveStatus('error');
    } finally {
      setIsEditorLoading(false);
    }
  };

  const handleSavePromptTemplate = async (type) => {
    try {
      setIsEditorLoading(true);
      const result = await GuardrailsService.updatePromptTemplate(expandedConfig, type, promptTemplates[type]);
      if (result.success === false) {
        console.warn(`Guardrails service error for ${type}:`, result.error);
      }
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.warn(`Error saving ${type} template:`, error.message);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setIsEditorLoading(false);
    }
  };

  const handleTestGuardrails = async () => {
    if (!expandedConfig || !testInput.trim()) return;
    try {
      setIsTesting(true);
      setTestResult(null);
      const result = await GuardrailsService.testGuardrails(expandedConfig, testType, testInput);
      setTestResult(result);
    } catch (error) {
      console.error('Error testing guardrails:', error);
      setTestResult({ success: false, error: error.message });
    } finally {
      setIsTesting(false);
    }
  };

  const updateInstruction = (index, field, value) => {
    const updated = [...configData.instructions];
    updated[index] = { ...updated[index], [field]: value };
    setConfigData({ ...configData, instructions: updated });
  };

  const handleUpdatePromptTemplate = (type, value) => {
    setPromptTemplates({ ...promptTemplates, [type]: value });
  };

  const renderInlineEditor = () => (
    <div className="border-t border-gray-200 bg-white">
      {/* Editor sub-header: tabs + save */}
      <div className="px-4 sm:px-6 pt-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-gray-600"></span>
          <div className="flex items-center gap-2">
            {saveStatus === 'success' && (
              <div className="flex items-center text-green-600 bg-green-50 px-3 py-1 rounded-full text-sm">
                <CheckCircle size={14} className="mr-1" /> Saved
              </div>
            )}
            {saveStatus === 'error' && (
              <div className="flex items-center text-red-600 bg-red-50 px-3 py-1 rounded-full text-sm">
                <AlertTriangle size={14} className="mr-1" /> Error saving
              </div>
            )}
            <button
              className="h-10 px-4 py-2 text-sm font-semibold text-error border border-red-200 hover:bg-red-50 hover:border-red-400 rounded-lg flex items-center disabled:opacity-50"
              onClick={() => handleDeleteConfig(expandedConfig)}
              disabled={isEditorLoading}
            >
              <Trash2 size={15} className="mr-1.5" />
              Delete
            </button>
            <button
              className="h-10 px-4 py-2 text-sm font-semibold bg-secondary text-white rounded-lg flex items-center hover:bg-secondaryDark disabled:opacity-50"
              onClick={handleSaveConfig}
              disabled={isEditorLoading}
            >
              <Save size={15} className="mr-1.5" />
              Save
            </button>
          </div>
        </div>
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'instructions', label: 'Instructions' },
            { id: 'prompts', label: 'Prompt Templates' },
            { id: 'test', label: 'Test Guardrails' },
          ].map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`tab-button py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div className="p-4 sm:p-6">
        {isEditorLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {activeTab === 'instructions' && (
              <InstructionsTab
                instructions={configData.instructions}
                onUpdateInstruction={updateInstruction}
              />
            )}
            {activeTab === 'prompts' && (
              <PromptTemplatesTab
                promptTemplates={promptTemplates}
                onUpdatePromptTemplate={handleUpdatePromptTemplate}
                onSavePromptTemplate={handleSavePromptTemplate}
              />
            )}
            {activeTab === 'test' && (
              <div className="space-y-4">
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Test Type</label>
                    <select
                      value={testType}
                      onChange={(e) => setTestType(e.target.value)}
                      className="w-full p-2 border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                      <option value="input">Input Guardrails</option>
                      <option value="output">Output Guardrails</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Test Text</label>
                    <textarea
                      value={testInput}
                      onChange={(e) => setTestInput(e.target.value)}
                      placeholder="Enter text to test guardrails..."
                      rows={6}
                      className="w-full p-3 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary font-mono text-sm"
                    />
                  </div>
                  <button
                    onClick={handleTestGuardrails}
                    disabled={isTesting || !testInput.trim()}
                    className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primaryDark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isTesting ? 'Testing...' : 'Run Test'}
                  </button>
                </div>

                {testResult && (
                  <div className="mt-6 space-y-4">
                    <h4 className="text-base font-semibold text-gray-600 uppercase tracking-wide">Test Result:</h4>
                    {testResult.success === false ? (
                      <div className="space-y-3">
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
                          <p className="font-medium">Error:</p>
                          <p className="text-sm mt-1">{testResult.error}</p>
                          {testResult.details && <p className="text-xs mt-2 text-red-600">{testResult.details}</p>}
                        </div>
                        {testResult.request && (
                          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                            <h5 className="text-sm font-semibold text-gray-600 mb-2">Request Sent:</h5>
                            <pre className="bg-white p-3 rounded border border-gray-300 overflow-auto text-xs">
{JSON.stringify(testResult.request, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5" />
                            <span className="font-medium">Guardrails API Response Received</span>
                          </div>
                          <p className="text-sm mt-1">Test completed at {new Date(testResult.timestamp).toLocaleString()}</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="p-4 bg-white border border-gray-200 rounded-lg">
                            <h5 className="text-sm font-semibold text-gray-600 mb-2">Test Configuration</h5>
                            <div className="space-y-2 text-sm">
                              <div><span className="font-medium">Config:</span> {testResult.config_name}</div>
                              <div><span className="font-medium">Check Type:</span> {testResult.test_type}</div>
                            </div>
                          </div>
                          <div className="p-4 bg-white border border-gray-200 rounded-lg">
                            <h5 className="text-sm font-semibold text-gray-600 mb-2">API Details</h5>
                            <div className="space-y-2 text-sm">
                              <div><span className="font-medium">Endpoint:</span> POST /guardrails</div>
                              <div><span className="font-medium">Status:</span> <span className="text-green-600">Success</span></div>
                            </div>
                          </div>
                        </div>
                        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <h5 className="font-semibold text-gray-700 mb-2">API Response</h5>
                          {testResult.is_json && testResult.api_response ? (
                            <div className="space-y-3">
                              {testResult.api_response.guardrails_response && (
                                <div className="bg-white p-4 rounded border border-gray-300">
                                  <p className="text-xs font-medium text-gray-600 mb-2">Guardrails Response:</p>
                                  <div className="text-sm text-gray-900 whitespace-pre-wrap">{testResult.api_response.guardrails_response}</div>
                                </div>
                              )}
                              {testResult.api_response.llm_decision !== undefined && (
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="bg-white p-3 rounded border border-gray-300">
                                    <p className="text-xs font-medium text-gray-600 mb-1">LLM Decision:</p>
                                    <div className={`text-lg font-semibold ${testResult.api_response.llm_decision === 'No' ? 'text-green-600' : 'text-red-600'}`}>
                                      {testResult.api_response.llm_decision === 'No' ? 'PASS ✓' : 'BLOCKED ✗'}
                                    </div>
                                  </div>
                                  <div className="bg-white p-3 rounded border border-gray-300">
                                    <p className="text-xs font-medium text-gray-600 mb-1">Block Reason:</p>
                                    <div className="text-sm text-gray-900">{testResult.api_response.block_explanation || 'None'}</div>
                                  </div>
                                </div>
                              )}
                              <details className="bg-white p-3 rounded border border-gray-300">
                                <summary className="text-xs font-medium text-gray-600 cursor-pointer">Full JSON Response</summary>
                                <pre className="mt-2 text-xs overflow-auto max-h-96">
{JSON.stringify(testResult.api_response, null, 2)}
                                </pre>
                              </details>
                            </div>
                          ) : (
                            <div className="bg-white p-4 rounded border border-gray-300 overflow-auto">
                              {testResult.api_response ? (
                                <div className="text-sm whitespace-pre-wrap">
                                  {typeof testResult.api_response === 'string'
                                    ? testResult.api_response
                                    : JSON.stringify(testResult.api_response, null, 2)}
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500 italic">Empty response received from API</div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <h5 className="font-semibold text-gray-700 mb-2">Request Sent</h5>
                          <pre className="bg-white p-4 rounded border border-gray-300 overflow-auto text-xs">
{JSON.stringify(testResult.request_sent, null, 2)}
                          </pre>
                        </div>
                        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <h5 className="font-semibold text-gray-700 mb-2">Test Input</h5>
                          <div className="bg-white p-4 rounded border border-gray-300 overflow-auto text-sm whitespace-pre-wrap">
                            {testResult.test_text}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Section 1 — Summary */}
      <div className="bg-gray-50 py-4 mb-5">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-[14px] font-semibold text-gray-600 uppercase tracking-[0.7px]">Configuration Summary</h2>
          <button
            className="bg-secondary text-white h-10 px-4 py-2 rounded-lg text-sm font-semibold flex items-center justify-center whitespace-nowrap hover:bg-secondaryDark"
            onClick={() => setShowNewConfigModal(true)}
          >
            <Plus size={18} className="mr-2" />
            Define Guardrails
          </button>
        </div>

        {!isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white border border-gray-200 p-3 sm:p-4 rounded-lg transition-all duration-300">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-primary font-medium text-sm sm:text-base">Total Configurations</h3>
                <Save size={18} className="text-primary" />
              </div>
              <div className="text-lg sm:text-xl font-bold">{configurations.length}</div>
              <div className="text-xs text-gray-500 mt-1">Active guardrail configurations</div>
            </div>
            <div className="bg-white border border-gray-200 p-3 sm:p-4 rounded-lg transition-all duration-300">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-primary font-medium text-sm sm:text-base">Last Modified</h3>
                <CheckCircle size={18} className="text-primary" />
              </div>
              <div className="text-lg sm:text-xl font-bold">
                {configurations.length > 0
                  ? new Date(Math.max(...configurations.map(c => new Date(c.lastModified)))).toLocaleDateString()
                  : '—'}
              </div>
              <div className="text-xs text-gray-500 mt-1">Most recently updated config</div>
            </div>
            <div className="bg-white border border-gray-200 p-3 sm:p-4 rounded-lg transition-all duration-300">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-primary font-medium text-sm sm:text-base">Check Types</h3>
                <AlertTriangle size={18} className="text-primary" />
              </div>
              <div className="text-lg sm:text-xl font-bold">3</div>
              <div className="text-xs text-gray-500 mt-1">Input · Output · Hallucination</div>
            </div>
          </div>
        )}
      </div>

      {/* Section 2 — Configurations list */}
      <div className="bg-gray-50 py-4">
        <h2 className="text-[14px] font-semibold text-gray-600 uppercase tracking-[0.7px] mb-3">Guardrail Configurations</h2>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-700">Loading configurations...</p>
        </div>
      ) : configurations.length === 0 ? (
        <div className="text-center py-10 bg-gray-50 rounded-lg border border-gray-200">
          <div className="text-gray-500 mb-2">No configurations found</div>
          <button
            className="bg-secondary text-white px-4 py-2 rounded-md flex items-center mx-auto hover:bg-secondaryDark"
            onClick={() => setShowNewConfigModal(true)}
          >
            <Plus size={18} className="mr-2" />
            Create Your First Configuration
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {configurations.map((config) => (
            <div key={config.useCase} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {/* Row */}
              <div
                className="flex items-center justify-between px-4 sm:px-6 py-3 cursor-pointer transition-colors hover:bg-gray-50"
                onClick={() => handleEditConfig(config.useCase)}
              >
                <div>
                  <div className="text-sm font-semibold text-gray-800">{config.useCase}</div>
                  <div className="text-xs text-gray-400 mt-0.5">Last modified: {new Date(config.lastModified).toLocaleString()}</div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); handleEditConfig(config.useCase); }}
                  className="p-1.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
                  title={expandedConfig === config.useCase ? 'Collapse' : 'Expand'}
                >
                  {expandedConfig === config.useCase ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
              </div>

              {/* Inline editor panel */}
              {expandedConfig === config.useCase && renderInlineEditor()}
            </div>
          ))}
        </div>
      )}

      </div>

      <NewConfigModal
        isOpen={showNewConfigModal}
        configName={newConfigName}
        onConfigNameChange={setNewConfigName}
        onClose={() => setShowNewConfigModal(false)}
        onCreate={handleCreateConfig}
        isLoading={isLoading}
      />
    </>
  );
};

export default GuardrailsManager;
